import MathHelper from './MathHelper';
import PieceHelper from './PieceHelper';
import { INITIAL_PATTERN_WIDTH, INITIAL_PATTERN_HEIGHT, NAVIGATOR_WIDTH, SIDEBAR_WIDTH, RULER_WIDTH } from '../constants/numbers';
import TextBoxHelper from './TextBoxHelper';
import SegmentHelper from './SegmentHelper';

const initialPatternSize = { width: INITIAL_PATTERN_WIDTH, height: INITIAL_PATTERN_HEIGHT };
export default class PatternHelper {
  static createPattern(title = 'New Pattern', description = '', pieces = {}, texts = {}, categories = [], size = initialPatternSize) {
    const photos = [];
    return {
      title,
      description,
      settings: {title, description, photos, categories},
      pieces: this.addSpecialDataStructures(pieces),
      texts,
      guides: {},
      size,
      photos,
    };
  }

  static addSpecialDataStructures(pieces) {
    const keys = Object.keys(pieces);
    const modifiedPieces = pieces;
    // eslint-disable-next-line no-restricted-syntax
    for (const key of keys) {
      // new Data structures to add.
      modifiedPieces[key] = {
        ...modifiedPieces[key],
        piecesInside: new Set(),
      };
    }
    return modifiedPieces;
  }

  static getLabels(pieces, excludeMatches = false, excludeExtensions = false, excludeLabelsWithExtensions = false) {
    let patternLabels = pieces.reduce((labels, piece) => [...labels, ...PieceHelper.getLabels(piece, excludeLabelsWithExtensions)], []);
    const filterLabels = (labels, filterArray) => labels.filter((label) => !filterArray.find((filterLabel) => filterLabel === label));

    if (excludeMatches) patternLabels = filterLabels(patternLabels, this.getMatches(pieces));

    if (excludeExtensions) patternLabels = filterLabels(patternLabels, this.getExtensions(pieces));

    return patternLabels;
  }

  static getExtensions(pieces) {
    return pieces.reduce((extensions, piece) => [...extensions, ...PieceHelper.getExtensions(piece)], []);
  }

  static getMatches(pieces) {
    return pieces.reduce((matchLabels, piece) => [...matchLabels, ...PieceHelper.getMatches(piece)], []);
  }

  static getLabel(pieces, label) {
    let result = null;

    if (label === '') return null;

    pieces.forEach((piece) => {
      const pieceLabel = PieceHelper.getLabel(piece, label);

      if (pieceLabel) {
        result = {
          pieceId: piece.id,
          label: pieceLabel,
        };
      }
    });

    return result;
  }

  static getLabelTotalLength(pieces, pieceId, label) {
    const { knotId, extensions } = label;
    const labelPiece = pieces.find((piece) => piece.id === pieceId);
    let totalLength = SegmentHelper.getLength(PieceHelper.getSegment(labelPiece, knotId));

    extensions.forEach((extension) => {
      pieces.forEach((piece) => {
        const pieceLabel = PieceHelper.getLabel(piece, extension);

        if (pieceLabel) {
          const segment = PieceHelper.getSegment(piece, pieceLabel.knotId);

          totalLength += SegmentHelper.getLength(segment);
        }
      });
    });

    return totalLength;
  }

  static isJsonCompatible(json) {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      // Closure to capture the file information.
      function verifyJson(file) {
        return (e) => {
          try {
            const jsonPattern = JSON.parse(e.target.result);
            if (jsonPattern.pieces) {
              resolve({ isCompatible: true });
            } else {
              resolve({ isCompatible: false });
            }
          } catch (ex) {
            reject(`ex when trying to parse json = ${ex}`);
          }
        }
      }
      reader.onload = verifyJson(json);
      reader.readAsText(json);
    });
  }

  static getExtensionTipKnots(pieces, extensions) {
    const extensionTipKnots = [];

    extensions.forEach((extension) => {
      pieces.forEach((piece) => {
        const pieceLabel = PieceHelper.getLabel(piece, extension);

        if (pieceLabel) {
          const segment = PieceHelper.getSegment(piece, pieceLabel.knotId);

          if (pieceLabel.inverse) {
            extensionTipKnots.push({
              start: segment[segment.length - 1],
              end: segment[0],
            });
          } else {
            extensionTipKnots.push({
              start: segment[0],
              end: segment[segment.length - 1],
            });
          }
        }
      });
    });

    return extensionTipKnots;
  }

  static getExtensionKnot(pieces, label, start = true) {
    let knot = null;

    pieces.forEach((piece) => {
      const pieceLabel = PieceHelper.getLabel(piece, label);

      if (pieceLabel) {
        const segment = PieceHelper.getSegment(piece, pieceLabel.knotId);
        if ((start && !pieceLabel.inverse) || (!start && pieceLabel.inverse)) {
          [knot] = segment;
        } else {
          knot = segment[segment.length - 1];
        }
      }
    });

    return knot;
  }

  static getLabelStartKnot(pieces, label) {
    let knot = null;

    pieces.forEach((piece) => {
      const pieceLabel = PieceHelper.getLabel(piece, label);

      if (!pieceLabel) return;

      const segment = PieceHelper.getSegment(piece, pieceLabel.knotId);
      const pieceMatch = piece.matches.find((m) => m.knotId === pieceLabel.knotId);
      const lastExtension = PieceHelper.getLastExtension(piece, pieceLabel.knotId);

      if (pieceMatch && pieceMatch.inverse) {
        if (lastExtension) {
          knot = this.getExtensionKnot(pieces, lastExtension, false);
        } else {
          knot = pieceLabel.inverse ? segment[0] : segment[segment.length - 1];
        }
      } else {
        knot = pieceLabel.inverse ? segment[segment.length - 1] : segment[0];
      }
    });

    return knot;
  }

  static getLabelEndKnot(pieces, label) { // TODO: Generalize...
    let knot = null;

    pieces.forEach((piece) => {
      const pieceLabel = PieceHelper.getLabel(piece, label);

      if (!pieceLabel) return;

      const segment = PieceHelper.getSegment(piece, pieceLabel.knotId);
      const pieceMatch = piece.matches.find((m) => m.knotId === pieceLabel.knotId);
      const lastExtension = PieceHelper.getLastExtension(piece, pieceLabel.knotId);

      if (pieceMatch && pieceMatch.inverse) {
        knot = pieceLabel.inverse ? segment[segment.length - 1] : segment[0];
      } else if (lastExtension) {
        knot = this.getExtensionKnot(pieces, lastExtension, false);
      } else {
        knot = pieceLabel.inverse ? segment[0] : segment[segment.length - 1];
      }
    });

    return knot;
  }

  static getLabelForExtension(pieces, label) {
    for (let i = 0; i < pieces.length; i += 1) {
      const piece = pieces[i];

      for (let j = 0; j < piece.labels.length; j += 1) {
        const { extensions } = piece.labels[j];

        if (extensions && extensions.find((extension) => extension === label)) {
          return {
            pieceId: piece.id,
            label: piece.labels[j],
          };
        }
      }
    }

    return null;
  }

  static getBounds({ pieces, texts }, includeTexts = true, border = { x: 0, y: 0 }) {
    let xMin = Number.MAX_VALUE;
    let yMin = Number.MAX_VALUE;
    let xMax = Number.MIN_VALUE;
    let yMax = Number.MIN_VALUE;

    Object.values(pieces).forEach((piece) => {
      const bounds = PieceHelper.getBounds(piece);
      const { x, y, w, h } = bounds;

      xMin = xMin < x ? xMin : x;
      xMax = xMax > x + w ? xMax : x + w;
      yMin = yMin < y ? yMin : y;
      yMax = yMax > y + h ? yMax : y + h;
    });

    if (includeTexts && texts) {
      Object.values(texts).forEach((text) => {
        const bounds = TextBoxHelper.getBounds(text);
        const { x, y, w, h } = bounds;

        xMin = xMin < x ? xMin : x;
        xMax = xMax > x + w ? xMax : x + w;
        yMin = yMin < y ? yMin : y;
        yMax = yMax > y + h ? yMax : y + h;
      });
    }

    if (xMin === Number.MAX_VALUE || yMin === Number.MAX_VALUE
      || xMax === Number.MIN_VALUE || yMax === Number.MIN_VALUE) {
      return { x: 0, y: 0, w: 0, h: 0 };
    }

    xMin -= border.x;
    xMax += border.x;
    yMin -= border.y;
    yMax += border.y;

    return { x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin };
  }

  static getOffsetAndScaleToFitBounds({ x, y, w, h }, viewportSize, margin = { x: 0, y: 0 }, isSideBarVisible = false) {
    if (!w || !h) {
      return { scale: 1, offset: { x: 0, y: 0 } };
    }

    let offset = { x: x - margin.x, y: y - margin.y };

    const newW = (w === 0 ? w + 1 : w) + (2 * margin.x);
    const newH = (h === 0 ? h + 1 : h) + (2 * margin.y);

    const panelWidth = isSideBarVisible ? SIDEBAR_WIDTH : NAVIGATOR_WIDTH;
    const width = viewportSize.width - RULER_WIDTH - panelWidth;
    const height = viewportSize.height - RULER_WIDTH;

    let scale = 1;

    if (width / newW < height / newH) {
      scale = width / newW;

      offset.y += RULER_WIDTH;
      offset.y -= ((height / scale) - (newH - (2 * RULER_WIDTH))) / 2;
    } else {
      scale = height / newH;

      offset.x += RULER_WIDTH;
      offset.x -= ((width / scale) - (newW - (2 * RULER_WIDTH))) / 2;
    }

    offset = MathHelper.scalarMultiplicationPoint(offset, scale);

    if (offset.x < 0) offset.x = 0;
    if (offset.y < 0) offset.y = 0;

    return { scale, offset };
  }

  static setSizeAndMatchesForAddedPieces(currentPieces, newPiecesToMatch) {
    const knownLabels = ['F_ARM_HOLE', 'F_NECK_HOLE', 'F_HEM_LINE'];
    // take all labels of current pattern pieces
    const patternLabels = [].concat(...currentPieces.map(({ id: pieceId, labels }) => labels.map((label) => ({ ...label, pieceId }))));

    // take all labels to match of all new pieces in a object
    const matchesForKnownLabels = Object.assign({}, ...newPiecesToMatch.map(({ labels, matches }) => {
      // map labels of piece. key = label.knotId & value = label
      const labelsObject = Object.assign({}, ...labels.map((label) => ({ [label.knotId]: label })));
      // valid if a match has a know label
      const hasKnowLabel = ({ label }) => knownLabels.find((knowLabel) => knowLabel === label);
      // valida if a match reference a segment with label
      const referenceToSegmentWithLabel = ({ knotId }) => labelsObject[knotId];

      return Object.assign({},
        ...matches.filter(hasKnowLabel)
          .filter(referenceToSegmentWithLabel)
          // { [match label]: { label: (segment label referenced by match knotId) }, length: (length of label's segment plus extensions) }
          .map(({ label, knotId }) => ({ [label]: { label: labelsObject[knotId].label }, length: undefined })));
    }));

    // create of update matches of current pieces from new pieces matches for know labels
    const matchedCurrentPieces = currentPieces.map((piece) => {
      // take all piece labels referenced by new pieces matches for know labels
      const pieceLabelsWithMatchLabel = patternLabels.filter((patternLabel) => patternLabel.pieceId === piece.id)
        .map((pieceLabel) => ({ pieceLabel, matchLabel: matchesForKnownLabels[pieceLabel.label] }))
        .filter(({ matchLabel }) => matchLabel);

      // set length (length of label's segment plus extensions) to new pieces matches for know labels
      pieceLabelsWithMatchLabel.forEach(({ pieceLabel }) => {
        const labelSegmentLength = this.getLabelTotalLength(currentPieces, piece.id, pieceLabel);
        matchesForKnownLabels[pieceLabel.label].length = labelSegmentLength;
      });

      // copy current piece matches
      const matches = piece.matches.slice();

      // update or add matches in copied current piece matches
      pieceLabelsWithMatchLabel.forEach(({ pieceLabel, matchLabel }) => {
        const match = { knotId: pieceLabel.knotId, label: matchLabel.label, inverse: false };
        const matchIndex = piece.matches.findIndex((m) => m.label === pieceLabel.label);

        if (matchIndex >= 0) matches[matchIndex] = match;
        else matches.push(match);
      });

      // new piece with new matches
      return { ...piece, matches };
    });

    const scaledNewPieces = newPiecesToMatch.map((piece) => {
      const pieceMatch = piece.matches.find((match) => matchesForKnownLabels[match.label]);

      if (!pieceMatch) return piece;

      // take segment lenght to match of new piece
      const segmentLength = SegmentHelper.getLength(PieceHelper.getSegment(piece, pieceMatch.knotId));
      // divie total length (length of label's segment plus extensions) of current piece label to match in segment length of new piece
      // if division is worng (for example total length is undefined) then return 1
      const scale = (matchesForKnownLabels[pieceMatch.label].length / segmentLength) || 1;
      const scaledPiece = PieceHelper.scaleAxis(piece, scale);

      return scaledPiece;
    });

    return [...matchedCurrentPieces, ...scaledNewPieces];
  }
  
  static extractGrade(pieces, grades, newBase) {
    const prevOffset = grades[newBase].offset;
    const piecesKeys = Object.keys(pieces);
    piecesKeys.forEach((pieceId) => {
      const centroid = PieceHelper.getCentroid(pieces[pieceId]);
      let knots = PieceHelper.getGradedKnots({centroid, knots:pieces[pieceId].knots, index:newBase, offset:prevOffset, isClosed:true});
      knots.forEach((knot, i) => {
        knot.grades = [];
      });
      pieces[pieceId].knots = knots;
    });
    return pieces;
  }

}
